import React, { useMemo } from "react";
import { Radar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const hexToRgba = (hex, alpha) => {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.slice(0, 2), 16) || 0;
  const g = parseInt(clean.slice(2, 4), 16) || 0;
  const b = parseInt(clean.slice(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const RadarAdapter = ({ spec, containerRef }) => {
  const { config = {}, data = [] } = spec;

  const chartData = useMemo(() => {
    const labels = config.labels || ["ChatGPT", "Claude", "Gemini", "Perplexity", "MetaAI"];
    const series = config.series || [{ key: "value", label: "Value" }];

    const s = typeof window !== "undefined" ? getComputedStyle(document.documentElement) : null;
    const get = (v) => (s ? s.getPropertyValue(v).trim() : "");
    // Default palette from BRANDING.md
    const colors = [
      get("--brand-color-1") || "#2dc6fe",
      get("--brand-color-2") || "#84fbdf",
      get("--brand-color-dark") || "#082631",
      get("--pink") || "#e63374",
    ];

    const datasets = series.map((sItem, sIdx) => {
      const color = sItem.color || colors[sIdx % colors.length] || "#0284c7";
      const dataRow = data[0] || {};
      const values = dataRow[sItem.key] || [];
      const fillOpacity = sItem.fillOpacity !== undefined ? sItem.fillOpacity : 0.28;

      return {
        label: sItem.label,
        data: values,
        backgroundColor: sItem.backgroundColor || hexToRgba(color, fillOpacity),
        borderColor: sItem.borderColor || color,
        borderWidth: sItem.borderWidth !== undefined ? sItem.borderWidth : 2.5,
        borderDash: sItem.borderDash || [],
        pointBackgroundColor: sItem.pointBackgroundColor || "#ffffff",
        pointBorderColor: sItem.pointBorderColor || color,
        pointBorderWidth: sItem.pointBorderWidth !== undefined ? sItem.pointBorderWidth : 2.5,
        pointRadius: sItem.pointRadius !== undefined ? sItem.pointRadius : 4.5,
        pointHoverRadius: sItem.pointHoverRadius !== undefined ? sItem.pointHoverRadius : 7,
        pointHoverBackgroundColor: sItem.pointHoverBackgroundColor || color,
        pointHoverBorderColor: sItem.pointHoverBorderColor || "#ffffff",
        pointHoverBorderWidth: sItem.pointHoverBorderWidth !== undefined ? sItem.pointHoverBorderWidth : 2,
      };
    });

    return {
      labels,
      datasets,
    };
  }, [data, config]);

  const options = useMemo(() => {
    const showLegend =
      config.displayLegend !== undefined
        ? config.displayLegend
        : (config.series?.length || 0) > 1;

    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: config.padding || { top: 6, bottom: 6, left: 10, right: 10 },
      },
      plugins: {
        legend: {
          display: showLegend,
          position: "top",
          labels: {
            font: { size: 11, family: "'Google Sans', sans-serif" },
            boxWidth: 12,
            color: "#475569",
          },
        },
        tooltip: {
          backgroundColor: "rgba(15, 23, 42, 0.94)",
          titleColor: "#f8fafc",
          bodyColor: "#f1f5f9",
          borderColor: "rgba(255, 255, 255, 0.12)",
          borderWidth: 1,
          padding: 10,
          cornerRadius: 8,
          boxPadding: 4,
          usePointStyle: true,
          callbacks: {
            label: (context) => {
              const label = context.dataset.label || "";
              const val = context.raw !== undefined ? context.raw : "";
              return ` ${label ? `${label}: ` : ""}${val}%`;
            },
          },
        },
      },
      scales: {
        r: {
          angleLines: {
            color: "rgba(226, 232, 240, 0.8)",
            lineWidth: 1,
          },
          grid: {
            color: "rgba(203, 213, 225, 0.6)",
            lineWidth: 1,
          },
          pointLabels: {
            font: {
              size: 11,
              weight: "600",
              family: "'Google Sans', sans-serif",
            },
            color: "#334155",
            padding: 8,
          },
          ticks: {
            backdropColor: "transparent",
            color: "#94a3b8",
            font: { size: 9 },
            stepSize: config.stepSize ?? 25,
            showLabelBackdrop: false,
          },
          suggestedMin: 0,
          suggestedMax: config.suggestedMax ?? 100,
        },
      },
    };
  }, [config]);

  const height = config.height || 300;

  return (
    <div ref={containerRef} className="assistant-chart-render-wrapper radar-render-wrapper" style={{ height: `${height}px` }}>
      <Radar data={chartData} options={options} />
    </div>
  );
};

export default RadarAdapter;
